import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";

const SCANNER_REGION_ID = "qr-scanner-region";
const SCAN_FPS = 10;
const TORCH_CONSTRAINT = { advanced: [{ torch: true }] };

/** Pick the rear camera if we can identify one, otherwise the last camera. */
function pickRearCamera(cameras) {
  if (!cameras?.length) return null;
  const rear =
    cameras.find((c) => /back|rear|environment|world|0/i.test(c.label)) ||
    cameras[cameras.length - 1];
  return rear || cameras[0];
}

function describeError(err) {
  const name = err?.name || "";
  const msg = err?.message || String(err) || "";
  if (name === "NotAllowedError" || /denied|permission/i.test(msg)) {
    return "Camera permission was denied. Open your browser settings, allow camera for this site, then tap Try again.";
  }
  if (name === "NotFoundError" || /no camera|notfound/i.test(msg)) {
    return "No camera found on this device. Use manual code entry instead.";
  }
  if (name === "NotReadableError" || /in use|busy/i.test(msg)) {
    return "The camera is already in use by another app. Close other camera apps and tap Try again.";
  }
  if (name === "OverconstrainedError") {
    return "This camera doesn't support the requested mode. Tap Try again — we'll pick a different one.";
  }
  if (name === "SecurityError" || /secure context|https/i.test(msg)) {
    return "The camera needs a secure HTTPS connection. If you're using a custom domain, contact your administrator.";
  }
  return msg || "Could not start the camera.";
}

/**
 * useQrScanner — tap-to-start QR scanner with explicit camera enumeration,
 * error surfacing and proper cleanup. Returns a state machine and a `start`
 * function that must be invoked from a user gesture (iOS Safari requirement).
 */
export default function useQrScanner({ onDecoded }) {
  // states: "idle" | "starting" | "scanning" | "error"
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const scannerRef = useRef(null);
  const stoppedRef = useRef(false);

  const stop = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      if (scanner.isScanning) await scanner.stop();
      await scanner.clear();
    } catch (err) {
      console.warn("Scanner stop failed:", err);
    }
    setTorchOn(false);
    setTorchSupported(false);
  }, []);

  const start = useCallback(async () => {
    setStatus("starting");
    setError("");
    stoppedRef.current = false;

    try {
      // Make sure prior instances are gone.
      await stop();

      // Permission probe first — this is the call iOS Safari ties to the user gesture.
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) {
        throw new Error("No camera devices available");
      }
      const cam = pickRearCamera(cameras);

      const scanner = new Html5Qrcode(SCANNER_REGION_ID, /* verbose */ false);
      scannerRef.current = scanner;

      const containerEl = document.getElementById(SCANNER_REGION_ID);
      const w = containerEl?.clientWidth || 320;
      const qrSize = Math.max(220, Math.floor(w * 0.7));

      await scanner.start(
        cam.id,
        {
          fps: SCAN_FPS,
          qrbox: { width: qrSize, height: qrSize },
          aspectRatio: 1.0,
        },
        async (decoded) => {
          if (stoppedRef.current) return;
          stoppedRef.current = true;
          try {
            await stop();
          } finally {
            onDecoded?.(decoded);
          }
        },
        () => {} // ignore per-frame decode misses
      );

      // Detect torch / flash support post-start.
      try {
        const capabilities = scanner.getRunningTrackCapabilities?.() || {};
        if ("torch" in capabilities) setTorchSupported(true);
      } catch (err) {
        // Optional capability — never fatal.
      }

      setStatus("scanning");
    } catch (err) {
      console.error("Camera start failed:", err);
      setError(describeError(err));
      setStatus("error");
      await stop();
    }
  }, [onDecoded, stop]);

  const toggleTorch = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner?.isScanning) return;
    try {
      await scanner.applyVideoConstraints(
        torchOn ? { advanced: [{ torch: false }] } : TORCH_CONSTRAINT
      );
      setTorchOn((v) => !v);
    } catch (err) {
      console.warn("Torch toggle failed:", err);
    }
  }, [torchOn]);

  /**
   * Decode a QR from a still image (taken from the OS native camera via
   * <input type="file" capture="environment"> or chosen from the gallery).
   * Works on virtually every device because it bypasses live camera streaming.
   * Returns the decoded string or throws.
   */
  const scanFile = useCallback(async (file) => {
    if (!file) throw new Error("No file");
    await stop();
    // scanFile needs its own element; reuse the same DOM region.
    const scanner = new Html5Qrcode(SCANNER_REGION_ID, false);
    try {
      const decoded = await scanner.scanFile(file, /* showImage */ false);
      return decoded;
    } finally {
      try {
        await scanner.clear();
      } catch (err) {
        // Non-fatal cleanup error
      }
    }
  }, [stop]);

  // Clean up on unmount.
  useEffect(() => {
    return () => {
      stoppedRef.current = true;
      stop();
    };
  }, [stop]);

  return {
    regionId: SCANNER_REGION_ID,
    status,
    error,
    torchOn,
    torchSupported,
    start,
    stop,
    toggleTorch,
    scanFile,
  };
}
